import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FilterState } from '../components/WorkOrderList';
import { WorkOrder, WorkOrderStatus } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { normalizeDateToYYYYMMDD } from '@/utils/dateUtils';

interface UseOrderHandlersProps {
    orders: WorkOrder[];
    selectedOrder: WorkOrder | null;
    setSelectedOrder: (order: WorkOrder | null) => void;
    updateOrder: (order: WorkOrder) => Promise<void>;
    deleteOrders: (orderIds: string[]) => Promise<void>;
    userRole: 'admin' | 'technician' | null;
    setDashboardFilters: (f: FilterState) => void;
}

export function useOrderHandlers({
    orders,
    selectedOrder,
    setSelectedOrder,
    updateOrder,
    deleteOrders,
    userRole,
    setDashboardFilters
}: UseOrderHandlersProps) {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const handleUpdateOrder = useCallback((updatedOrder: WorkOrder) => {
        if (updatedOrder.status === WorkOrderStatus.PENDING) {
            const original = orders.find(o => o.id === updatedOrder.id);
            if (original && original.scheduledDate !== updatedOrder.scheduledDate) {
                // Solo restringir a técnicos; los administradores pueden programar en cualquier fecha (pasada, presente o futura)
                if (userRole !== 'admin') {
                    const targetDate = normalizeDateToYYYYMMDD(updatedOrder.scheduledDate);
                    const todayDate = normalizeDateToYYYYMMDD(new Date().toISOString());
                    if (targetDate && todayDate && targetDate < todayDate) {
                        showToast("No se puede programar una fecha anterior a la actual.", 'error');
                        return;
                    }
                }
            }
        }

        // V4.0 Unification Logic: If moving to COMPLETED for the first time
        const originalOrder = orders.find(o => o.id === updatedOrder.id);
        const isNowCompleted = originalOrder && originalOrder.status !== WorkOrderStatus.COMPLETED && updatedOrder.status === WorkOrderStatus.COMPLETED;

        if (isNowCompleted) {
            // V101: Simplificación de lógica. Ya no unimos varios WorkOrders paralelos,
            // porque todo el historial vive dentro del propio arreglo `laborJournal` de `updatedOrder`.
            console.log("Parte finalizado, todo el historial contenido en una misma orden.");
        }

        updateOrder(updatedOrder);
        if (selectedOrder?.id === updatedOrder.id) {
            setSelectedOrder(updatedOrder);
        }
        if (userRole === 'technician' && updatedOrder.status === WorkOrderStatus.COMPLETED) {
            showToast("Parte finalizado. Visible para revisión.", 'success');
        }
    }, [orders, selectedOrder, setSelectedOrder, updateOrder, userRole, showToast]);

    const handleDashboardNavigate = useCallback((type: 'status' | 'priority' | 'all' | 'tag', value: string) => {
        setDashboardFilters({
            status: type === 'status' ? value : null,
            priority: type === 'priority' ? value : null,
            client: null,
            tag: type === 'tag' ? value : null,
            technician: null,
            dateRange: null
        });
        navigate('/orders');
    }, [setDashboardFilters, navigate]);

    const handleNavigateToTechOrders = useCallback((technicianId: string) => {
        setDashboardFilters({
            status: null,
            priority: null,
            client: null,
            tag: null,
            technician: technicianId,
            dateRange: null
        });
        navigate('/orders');
    }, [setDashboardFilters, navigate]);

    return { handleUpdateOrder, handleDashboardNavigate, handleNavigateToTechOrders };
}
